import { FrostFreeStatus, HeatingNetworkFrostFreeStatus, type FrostFreePeriod } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";

import { FROST_FREE_WITHIN_DAYS } from "~/constants/frost-free.constant";

/** Rooms, buildings and heating networks all carry a status plus periods, with different status enums. */
export interface FrostFreeData {
  status: string;
  periods: FrostFreePeriod[];
}

/** Where an active (or planned) frost free comes from, from the widest scope to the narrowest. */
export enum FrostFreeSource {
  BUILDING = "building",
  HEATING_NETWORK = "heatingNetwork",
  /** Only part of the networks serving the room are in frost free. */
  HEATING_NETWORK_PARTIAL = "heatingNetwork-partial",
  /** Every room served by the network is in frost free, so the network follows. */
  ROOMS_ALL = "rooms-all",
  ROOM = "room",
}

export type BuildingFrostFreeSource = FrostFreeSource.BUILDING;

export type RoomFrostFreeSource =
  | FrostFreeSource.BUILDING
  | FrostFreeSource.HEATING_NETWORK
  | FrostFreeSource.HEATING_NETWORK_PARTIAL
  | FrostFreeSource.ROOM;

export type HeatingNetworkFrostFreeSource =
  | FrostFreeSource.BUILDING
  | FrostFreeSource.HEATING_NETWORK
  | FrostFreeSource.ROOMS_ALL;

interface FrostFreeActiveResult<TSource> {
  active: boolean;
  source: TSource;
  period?: FrostFreePeriod;
}

interface FrostFreePlannedResult<TSource> {
  planned: boolean;
  source: TSource;
}

/** Per-network detail, so a room alert can name the networks that put it in frost free. */
export type FrostFreeRoomHeatingNetworks = Record<string, { active: boolean; period?: FrostFreePeriod }>;

export type FrostFreeBuildingResult = FrostFreeActiveResult<BuildingFrostFreeSource>;
export type FrostFreePlannedBuildingResult = FrostFreePlannedResult<BuildingFrostFreeSource>;

export type FrostFreeRoomResult = FrostFreeActiveResult<RoomFrostFreeSource> & {
  heatingNetworks?: FrostFreeRoomHeatingNetworks;
};
export type FrostFreePlannedRoomResult = FrostFreePlannedResult<RoomFrostFreeSource> & {
  heatingNetworks?: FrostFreeRoomHeatingNetworks;
};

export type FrostFreeHeatingNetworkResult = FrostFreeActiveResult<HeatingNetworkFrostFreeSource> & {
  roomIds?: string[];
};
export type FrostFreePlannedHeatingNetworkResult = FrostFreePlannedResult<HeatingNetworkFrostFreeSource> & {
  roomIds?: string[];
};

interface FrostFreeMatch {
  matched: boolean;
  period?: FrostFreePeriod;
}

type FrostFreeCheck = (data?: FrostFreeData) => FrostFreeMatch;

/* -------------------------------------------------------------------------------------------------
 * Primitive checkers — operate on a single FrostFreeData
 * -----------------------------------------------------------------------------------------------*/
export const isFrostFreeActive = (data?: FrostFreeData): { active: boolean; period?: FrostFreePeriod } => {
  if (!data || data.status === FrostFreeStatus.DISABLED) return { active: false };
  if (data.status === FrostFreeStatus.ENABLED) return { active: true };

  const today = dayjs().startOf("day");
  for (const period of data.periods) {
    const start = dayjs(period.begin, "YYYY-MM-DD");
    const stop = dayjs(period.end, "YYYY-MM-DD");
    const isActive = start.isSame(stop, "day") ? today.isSame(start, "day") : today.isBetween(start, stop, null, "[]");
    if (isActive) return { active: true, period };
  }
  return { active: false };
};

export const hasFrostFreePlanned = (data?: FrostFreeData, withinDays = FROST_FREE_WITHIN_DAYS): boolean => {
  if (data?.status !== FrostFreeStatus.PERIODS) return false;
  const todayStart = dayjs().startOf("day");
  const limitDay = todayStart.add(withinDays, "day");
  return data.periods.some(p => {
    const begin = dayjs(p.begin, "YYYY-MM-DD").startOf("day");
    return begin.isAfter(todayStart) && !begin.isAfter(limitDay, "day");
  });
};

const activeCheck: FrostFreeCheck = data => {
  const { active, period } = isFrostFreeActive(data);
  return { matched: active, period };
};

const plannedCheck: FrostFreeCheck = data => ({ matched: hasFrostFreePlanned(data, FROST_FREE_WITHIN_DAYS) });

/* -------------------------------------------------------------------------------------------------
 * Building
 * -----------------------------------------------------------------------------------------------*/
export const getBuildingFrostFreeStatus = (buildingFrostFree: FrostFreeData): FrostFreeBuildingResult => {
  const { matched, period } = activeCheck(buildingFrostFree);
  return { active: matched, source: FrostFreeSource.BUILDING, ...(matched && period ? { period } : {}) };
};

export const getBuildingFrostFreePlanned = (buildingFrostFree: FrostFreeData): FrostFreePlannedBuildingResult => ({
  planned: plannedCheck(buildingFrostFree).matched,
  source: FrostFreeSource.BUILDING,
});

/* -------------------------------------------------------------------------------------------------
 * Room — building wins, then the networks serving the room, then the room itself
 * -----------------------------------------------------------------------------------------------*/
interface RoomFrostFreeOptions {
  buildingFrostFree: FrostFreeData;
  roomFrostFree: FrostFreeData;
  networksOfRoom?: { id: string; frostFree?: FrostFreeData }[];
}

type ResolvedRoom =
  | { matched: false }
  | {
      matched: true;
      source: RoomFrostFreeSource;
      period?: FrostFreePeriod;
      heatingNetworks?: FrostFreeRoomHeatingNetworks;
    };

const resolveRoomHierarchy = (
  { buildingFrostFree, roomFrostFree, networksOfRoom }: RoomFrostFreeOptions,
  check: FrostFreeCheck,
): ResolvedRoom => {
  const building = check(buildingFrostFree);
  if (building.matched) return { matched: true, source: FrostFreeSource.BUILDING, period: building.period };

  const matchedNetworks = (networksOfRoom ?? []).reduce<FrostFreeRoomHeatingNetworks>((acc, network) => {
    const result = check(network.frostFree);
    if (result.matched) acc[network.id] = { active: true, period: result.period };
    return acc;
  }, {});

  const matchedCount = Object.keys(matchedNetworks).length;
  if (matchedCount > 0) {
    return {
      matched: true,
      source:
        matchedCount === networksOfRoom?.length
          ? FrostFreeSource.HEATING_NETWORK
          : FrostFreeSource.HEATING_NETWORK_PARTIAL,
      heatingNetworks: matchedNetworks,
    };
  }

  const room = check(roomFrostFree);
  if (room.matched) return { matched: true, source: FrostFreeSource.ROOM, period: room.period };

  return { matched: false };
};

export const getRoomFrostFreeStatus = (options: RoomFrostFreeOptions): FrostFreeRoomResult => {
  const resolved = resolveRoomHierarchy(options, activeCheck);
  if (!resolved.matched) return { active: false, source: FrostFreeSource.ROOM };

  return {
    active: true,
    source: resolved.source,
    ...(resolved.period ? { period: resolved.period } : {}),
    ...(resolved.heatingNetworks ? { heatingNetworks: resolved.heatingNetworks } : {}),
  };
};

export const getRoomFrostFreePlanned = (options: RoomFrostFreeOptions): FrostFreePlannedRoomResult => {
  const resolved = resolveRoomHierarchy(options, plannedCheck);
  if (!resolved.matched) return { planned: false, source: FrostFreeSource.ROOM };

  return {
    planned: true,
    source: resolved.source,
    ...(resolved.heatingNetworks ? { heatingNetworks: resolved.heatingNetworks } : {}),
  };
};

/* -------------------------------------------------------------------------------------------------
 * Heating network — building wins, then the network itself, then all the rooms it serves
 * -----------------------------------------------------------------------------------------------*/
interface HeatingNetworkFrostFreeOptions {
  buildingFrostFree: FrostFreeData;
  networkFrostFree?: FrostFreeData;
  associatedRooms?: { id: string; frostFree: FrostFreeData }[];
}

type ResolvedHeatingNetwork =
  | { matched: false }
  | { matched: true; source: HeatingNetworkFrostFreeSource; period?: FrostFreePeriod; roomIds?: string[] };

const resolveHeatingNetworkHierarchy = (
  { buildingFrostFree, networkFrostFree, associatedRooms }: HeatingNetworkFrostFreeOptions,
  check: FrostFreeCheck,
): ResolvedHeatingNetwork => {
  const building = check(buildingFrostFree);
  if (building.matched) return { matched: true, source: FrostFreeSource.BUILDING, period: building.period };

  // In `auto` the network has no schedule of its own: it only follows the rooms it serves.
  if (networkFrostFree?.status !== HeatingNetworkFrostFreeStatus.AUTO) {
    const network = check(networkFrostFree);
    if (network.matched) return { matched: true, source: FrostFreeSource.HEATING_NETWORK, period: network.period };
  }

  if (associatedRooms && associatedRooms.length > 0 && associatedRooms.every(room => check(room.frostFree).matched)) {
    return { matched: true, source: FrostFreeSource.ROOMS_ALL, roomIds: associatedRooms.map(room => room.id) };
  }

  return { matched: false };
};

export const getHeatingNetworkFrostFreeStatus = (
  options: HeatingNetworkFrostFreeOptions,
): FrostFreeHeatingNetworkResult => {
  const resolved = resolveHeatingNetworkHierarchy(options, activeCheck);
  if (!resolved.matched) return { active: false, source: FrostFreeSource.HEATING_NETWORK };

  return {
    active: true,
    source: resolved.source,
    ...(resolved.period ? { period: resolved.period } : {}),
    ...(resolved.roomIds ? { roomIds: resolved.roomIds } : {}),
  };
};

export const getHeatingNetworkFrostFreePlanned = (
  options: HeatingNetworkFrostFreeOptions,
): FrostFreePlannedHeatingNetworkResult => {
  const resolved = resolveHeatingNetworkHierarchy(options, plannedCheck);
  if (!resolved.matched) return { planned: false, source: FrostFreeSource.HEATING_NETWORK };

  return {
    planned: true,
    source: resolved.source,
    ...(resolved.roomIds ? { roomIds: resolved.roomIds } : {}),
  };
};
